import { useState, useEffect } from 'react';
import Koerper from './Koerper';
import GymSession from './GymSession';
import RadSession from './RadSession';
import Ziele from './Ziele';
import VerlaufAnsicht from './Verlauf';
import StravaChart from './StravaChart';
import Atemschutz from './Atemschutz';
import { apiFetch, hashPassword } from './shared';

const API = '/api';

// ─── Auth-Gate ────────────────────────────────────────────────────────────────
function AuthGate({ children }) {
  const [status, setStatus] = useState('checking');
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const hash = localStorage.getItem('appPasswordHash');
    if (!hash) { setStatus('login'); return; }
    fetch('/api/auth/check', { headers: { 'X-App-Password': hash } })
      .then(r => r.ok ? setStatus('authenticated') : setStatus('login'))
      .catch(() => setStatus('login'));
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const hash = await hashPassword(pw);
      const r = await fetch('/api/auth/check', { headers: { 'X-App-Password': hash } });
      if (r.ok) {
        localStorage.setItem('appPasswordHash', hash);
        setStatus('authenticated');
      } else {
        setError('Falsches Passwort');
      }
    } catch {
      setError('Fehler beim Anmelden');
    }
    setLoading(false);
  }

  if (status === 'checking') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0d0d0d' }}>
        <div style={{ fontSize: 13, color: '#444' }}>...</div>
      </div>
    );
  }

  if (status === 'login') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0d0d0d', fontFamily: 'Georgia,serif' }}>
        <form onSubmit={handleLogin} style={{ width: 280, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: 4, color: '#555', textTransform: 'uppercase', marginBottom: 8 }}>Training</div>
          <input
            type="password"
            placeholder="Passwort"
            value={pw}
            onChange={e => setPw(e.target.value)}
            autoFocus
            style={{ background: '#141414', border: '1px solid #2a2a2a', borderRadius: 8, padding: '12px 14px', color: '#e0e0e0', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
          />
          {error && <div style={{ fontSize: 12, color: '#cf7f50' }}>{error}</div>}
          <button
            type="submit"
            disabled={loading || !pw}
            style={{ background: '#1a2a1a', border: '1px solid #2a4a2a', borderRadius: 8, padding: '12px', color: '#4a9c6f', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {loading ? '...' : 'Einloggen'}
          </button>
          <button
            type="button"
            onClick={() => window.history.back()}
            style={{ background: 'transparent', border: 'none', padding: '4px', color: '#444', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' }}
          >
            Abbrechen
          </button>
        </form>
      </div>
    );
  }

  return children;
}

// ─── Health-Import-Statusbox ──────────────────────────────────────────────────
function HealthImportStatus() {
  const [data, setData] = useState(null);

  useEffect(() => {
    apiFetch('/api/health-import/status')
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  if (!data) return null;

  const farbe = data.status === 'gruen' ? '#4a9c6f' : data.status === 'gelb' ? '#c17f24' : '#cf4f4f';

  const fmt = (iso) => {
    if (!iso) return '–';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' }) +
           ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const wert = (v, einheit) => v != null ? `${v}${einheit}` : '(fehlt)';
  const wertColor = (v) => v != null ? '#e0e0e0' : '#555';

  return (
    <div style={{ background:'#141414', border:`1px solid ${farbe}44`, borderRadius:8, padding:'14px 16px', marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ fontSize:10, color:'#555', letterSpacing:2 }}>HEALTH IMPORT</div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ color:farbe, fontSize:16, lineHeight:1 }}>●</span>
          <span style={{ fontSize:11, color:'#888' }}>
            Letzter Import: {data.letzterImport ? fmt(data.letzterImport) : '–'}
          </span>
        </div>
      </div>
      {data.daten ? (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10 }}>
          {[
            { label:'HRV',      val: wert(data.daten.hrv, ' ms'),     color: wertColor(data.daten.hrv) },
            { label:'Ruhepuls', val: wert(data.daten.ruhepuls, ' bpm'), color: wertColor(data.daten.ruhepuls) },
            { label:'SpO2',     val: wert(data.daten.spo2 != null ? Math.round(data.daten.spo2) : null, ' %'), color: wertColor(data.daten.spo2) },
            { label:'Gewicht',  val: wert(data.daten.gewicht, ' kg'),  color: wertColor(data.daten.gewicht) },
          ].map(({ label, val, color }) => (
            <div key={label}>
              <div style={{ fontSize:9, color:'#555', letterSpacing:1, marginBottom:3 }}>{label}</div>
              <div style={{ fontSize:13, color }}>{val}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize:12, color:'#555' }}>Keine Daten vorhanden</div>
      )}
      {data.datum && (
        <div style={{ fontSize:10, color:'#444', marginTop:8 }}>Stand: {data.datum}</div>
      )}
    </div>
  );
}

// ─── Plan-Tab: Empfehlungskarte + Gym/Rad-Ansicht ────────────────────────────
function PlanTab() {
  const [ansicht, setAnsicht] = useState('plan'); // 'plan'|'gym'|'rad'|'ziele'|'verlauf'|'wochenplan'|'atemschutz'
  const [empf, setEmpf] = useState(null);
  const [empfLoading, setEmpfLoading] = useState(true);
  const [fokus, setFokus] = useState('rad');

  useEffect(() => {
    apiFetch('/api/empfehlung/heute')
      .then(r => r.json())
      .then(setEmpf)
      .catch(() => {})
      .finally(() => setEmpfLoading(false));
    apiFetch('/api/einstellungen')
      .then(r => r.json())
      .then(d => { if (d.trainings_fokus) setFokus(d.trainings_fokus); })
      .catch(() => {});
  }, []);

  function saveFokus(neuerFokus) {
    setFokus(neuerFokus);
    apiFetch('/api/einstellungen', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ trainings_fokus: neuerFokus }),
    }).catch(() => {});
  }

  if (ansicht === 'gym')        return <GymSession   onClose={() => setAnsicht('plan')}/>;
  if (ansicht === 'rad')        return <RadSession    onClose={() => setAnsicht('plan')}/>;
  if (ansicht === 'verlauf')    return <VerlaufAnsicht onClose={() => setAnsicht('plan')}/>;
  if (ansicht === 'atemschutz') return <Atemschutz    onClose={() => setAnsicht('plan')}/>;

  const empfLabel = {
    plan: 'Plan wie geplant',
    zone2: 'Zone 2 / Erholung',
    pause: 'Pause heute',
    erhaltung: 'Erhaltungstraining',
  };
  const empfColor = {
    plan: '#4a9c6f',
    zone2: '#2a6fa8',
    pause: '#666',
    erhaltung: '#c17f24',
  };
  const col = empf ? (empfColor[empf.empfehlung] || '#888') : '#444';

  return (
    <div style={{display:'flex', flexDirection:'column', gap:12}}>
      {/* Empfehlungskarte */}
      <div style={{background:'#161616', border:'1px solid ' + col, borderRadius:8, padding:'14px 16px'}}>
        <div style={{fontSize:10, color:'#555', letterSpacing:2, marginBottom:6}}>EMPFEHLUNG HEUTE</div>
        {empfLoading ? (
          <div style={{fontSize:13, color:'#444'}}>Lade...</div>
        ) : empf ? (
          <>
            <div style={{fontSize:15, fontWeight:700, color: col, marginBottom:4}}>
              {empfLabel[empf.empfehlung] || empf.empfehlung}
            </div>
            <div style={{fontSize:12, color:'#888', lineHeight:1.6}}>{empf.begruendung}</div>
            <div style={{display:'flex', gap:6, marginTop:8, flexWrap:'wrap', fontSize:10}}>
              {/* Fokus-Badge */}
              {(() => {
                const fMap = { rad: ['Rad/Ausdauer','#5a8fd4','#0a0d1a'], feuerwehr: ['Feuerwehr','#c17f24','#1a0e0a'], longevity: ['Longevity','#9b6fe0','#150b21'] };
                const f = fMap[fokus];
                return f ? <span style={{background: f[2], border:`1px solid ${f[1]}`, borderRadius:3, padding:'2px 8px', color: f[1]}}>{f[0]}</span> : null;
              })()}
              {empf.hrv_status && (
                <span style={{background:'#1a1a1a', borderRadius:3, padding:'2px 8px', color: empf.hrv_status==='hoch'?'#4a9c6f': empf.hrv_status==='niedrig'?'#cf7f50':'#666'}}>
                  HRV {empf.hrv_status}
                </span>
              )}
              {empf.schlaf_status && (
                <span style={{background:'#1a1a1a', borderRadius:3, padding:'2px 8px', color: empf.schlaf_status==='gut'?'#4a9c6f': empf.schlaf_status==='schlecht'?'#cf4f4f':'#c17f24'}}>
                  Schlaf {empf.schlaf_status}
                </span>
              )}
              {empf.belastung_status && (
                <span style={{background:'#1a1a1a', borderRadius:3, padding:'2px 8px', color: empf.belastung_status==='niedrig'?'#4a9c6f': empf.belastung_status==='hoch'?'#cf7f50':'#666'}}>
                  Belastung {empf.belastung_status}
                </span>
              )}
            </div>
          </>
        ) : (
          <div style={{fontSize:12, color:'#555'}}>Keine Gesundheitsdaten vorhanden</div>
        )}

        {/* Trainings-Fokus */}
        <div style={{marginTop:14, padding:'10px 12px', background:'#0e0e0e', border:'1px solid #1e1e1e', borderRadius:7}}>
          <div style={{fontSize:9, color:'#444', letterSpacing:2, marginBottom:6}}>TRAININGS-FOKUS</div>
          <div style={{display:'flex', gap:6}}>
            {[
              {v:'rad',     l:'Rad / Ausdauer', c:'#0a0d1a', ac:'#1a2a4a', tc:'#5a8fd4'},
              {v:'feuerwehr', l:'Feuerwehr', c:'#1a0e0a', ac:'#4a2a1a', tc:'#c17f24'},
              {v:'longevity', l:'Longevity', c:'#150b21', ac:'#301a4a', tc:'#9b6fe0'},
            ].map(f => (
              <button key={f.v} onClick={() => saveFokus(f.v)}
                style={{flex:1, background: fokus===f.v ? f.ac : f.c, border:`1px solid ${fokus===f.v ? f.tc : '#2a2a2a'}`, borderRadius:5, padding:'6px 4px', fontSize:10, fontWeight: fokus===f.v ? 700 : 400, color: fokus===f.v ? f.tc : '#444', cursor:'pointer', fontFamily:'inherit'}}>
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {/* Aktions-Buttons */}
        <div style={{display:'flex', gap:8, marginTop:10, flexWrap:'wrap'}}>
          <button onClick={() => setAnsicht('gym')}
            style={{flex:1, minWidth:72, background:'#0d2010', border:'1px solid #2a5c2a', borderRadius:8, padding:'12px 8px', color:'#4a9c6f', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit'}}>
            Gym
          </button>
          <button onClick={() => setAnsicht('rad')}
            style={{flex:1, minWidth:72, background:'#090d1f', border:'1px solid #1a2a4a', borderRadius:8, padding:'12px 8px', color:'#5a8fd4', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit'}}>
            Rad
          </button>
        </div>
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          {fokus === 'feuerwehr' && (
            <button onClick={() => setAnsicht('atemschutz')}
              style={{flex:1, minWidth:120, background:'#1a0e0a', border:'1px solid #4a2a1a', borderRadius:8, padding:'10px 8px', color:'#c17f24', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit'}}>
              🔥 Atemschutz
            </button>
          )}
          <button onClick={() => setAnsicht('verlauf')}
            style={{flex:1, minWidth:72, background:'#111', border:'1px solid #2a2a2a', borderRadius:8, padding:'10px 8px', color:'#666', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit'}}>
            Verlauf
          </button>
        </div>
      </div>

    </div>
  );
}

function JahresVerlauf() {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiFetch(API + '/dashboard/jahresverlauf').then(r => r.ok ? r.json() : null).then(setData).catch(() => {});
  }, []);
  if (!data) return null;

  const { monate, jahr } = data;
  const aktuellerMonat = new Date().getMonth() + 1;
  const maxKm = Math.max(...monate.map(m => m.km), 1);
  const maxHm = Math.max(...monate.map(m => m.hm), 1);
  const labels = ['J','F','M','A','M','J','J','A','S','O','N','D'];
  const W = 320, H = 114, PL = 26, PR = 4, PT = 8, PB = 24;
  const cw = W - PL - PR, ch = H - PT - PB;
  const barW = cw / 12;

  // HM-Linie: Punkte für Polyline
  const hmPunkte = monate
    .map((m, i) => m.hm > 0 ? `${(PL + i * barW + barW / 2).toFixed(1)},${(PT + ch - (m.hm / maxHm) * ch).toFixed(1)}` : null)
    .filter(Boolean).join(' ');

  return (
    <div style={{background:'#141414', borderRadius:8, overflow:'hidden', marginBottom:16}}>
      <div style={{padding:'10px 16px', background:'#1c1c1c', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div style={{fontSize:10, color:'#555', letterSpacing:2}}>JAHRESVERLAUF {jahr}</div>
        <div style={{fontSize:9, color:'#444', display:'flex', gap:10}}>
          <span><span style={{color:'#253a52'}}>■</span> km</span>
          <span><span style={{color:'#7a4a2a'}}>—</span> hm</span>
          <span><span style={{color:'#2a5c3a'}}>■</span> Gym</span>
        </div>
      </div>
      <div style={{padding:'10px 12px 8px'}}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', height:'auto', display:'block'}}>
          <line x1={PL} y1={PT} x2={PL} y2={PT+ch} stroke="#1e1e1e" strokeWidth="1"/>
          <text x={PL-3} y={PT+5} textAnchor="end" fontSize="7" fill="#333">{maxKm}</text>
          <text x={PL-3} y={PT+ch/2+3} textAnchor="end" fontSize="7" fill="#333">{Math.round(maxKm/2)}</text>
          <line x1={PL} y1={PT+ch/2} x2={W-PR} y2={PT+ch/2} stroke="#1a1a1a" strokeWidth="0.5" strokeDasharray="2,2"/>
          {monate.map((m, i) => {
            const barH = maxKm > 0 ? (m.km / maxKm) * ch : 0;
            const x = PL + i * barW;
            const y = PT + ch - barH;
            const isCurrent = m.monat === aktuellerMonat;
            return (
              <g key={i}>
                <rect x={x+1.5} y={y} width={barW-3} height={barH || 0} fill={isCurrent ? '#4a7abf' : '#253a52'} rx="1.5"/>
                {m.km > 0 && barH > 14 && (
                  <text x={x+barW/2} y={y+10} textAnchor="middle" fontSize="6.5" fill={isCurrent ? '#9ab8df' : '#4a6a8a'}>{m.km}</text>
                )}
                {m.gym > 0 && (
                  <text x={x+barW/2} y={PT+ch+11} textAnchor="middle" fontSize="6.5" fill="#3a6a3a">{m.gym}×</text>
                )}
                <text x={x+barW/2} y={H-3} textAnchor="middle" fontSize="7.5" fill={isCurrent ? '#888' : '#333'}>{labels[i]}</text>
              </g>
            );
          })}
          {/* HM als Linie */}
          {hmPunkte && <polyline points={hmPunkte} fill="none" stroke="#8a5a2a" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.8"/>}
          {monate.map((m, i) => m.hm > 0 && (
            <circle key={i} cx={PL + i * barW + barW / 2} cy={PT + ch - (m.hm / maxHm) * ch} r="2" fill="#8a5a2a" opacity="0.8"/>
          ))}
        </svg>
      </div>
    </div>
  );
}

function ernaehrungsEmpfehlung(kcal) {
  if (!kcal || kcal <= 0) return null;
  if (kcal < 600) return {
    text: 'Kleiner Snack reicht — Joghurt + Haferflocken',
    farbe: '#4a7c59', bg: '#0d1f14',
  };
  if (kcal <= 1000) return {
    text: 'Größerer Snack + KH-betontes Abendessen — Joghurt + Haferflocken + Honig, Pasta/Reis am Abend',
    farbe: '#c17f24', bg: '#1f160a',
  };
  return {
    text: 'Carbo-Loading — doppelte Snack-Portion, KH-reich über den ganzen Abend verteilen',
    farbe: '#cf5050', bg: '#1f0d0d',
  };
}

function SchlafSnack() {
  const [data, setData] = useState(null);

  useEffect(() => {
    function laden() {
      apiFetch(API + '/dashboard/schlaf-snack')
        .then(r => r.json())
        .then(setData)
        .catch(() => {});
    }
    laden();
    const t = setInterval(laden, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (!data || !data.zeigen) return null;

  const farbe = data.snack_label === 'groß' ? '#cf5050'
    : data.snack_label === 'mittel' ? '#c17f24'
    : '#4a7c59';
  const bg = data.snack_label === 'groß' ? '#1f0d0d'
    : data.snack_label === 'mittel' ? '#1f160a'
    : '#0d1f14';
  const zeitBis = data.minuten_bis_schlaf > 0
    ? `${data.minuten_bis_schlaf} min bis ${data.schlaf_uhrzeit}`
    : `Schlafzeit (${data.schlaf_uhrzeit})`;

  return (
    <div style={{background: bg, border: `1px solid ${farbe}44`, borderLeft: `4px solid ${farbe}`, borderRadius: 8, padding: '14px 18px', marginBottom: 16}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8}}>
        <div style={{fontSize: 11, color: farbe, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700}}>
          Schlaf-Snack
        </div>
        <div style={{fontSize: 11, color: '#555'}}>{zeitBis}</div>
      </div>
      <div style={{fontSize: 13, color: '#e0e0e0', marginBottom: 4}}>{data.snack_text}</div>
      <div style={{fontSize: 11, color: '#555'}}>
        Tagesverbrauch {data.kcal_heute} kcal · Portion {data.snack_label}
      </div>
    </div>
  );
}

function App() {
  const [dash, setDash] = useState(null);
  const [woche, setWoche] = useState(null);
  const [monat, setMonat] = useState(null);
  const [jahr, setJahr] = useState(null);
  const [tab, setTab] = useState('ziele');
  const [stravaSync, setStravaSync] = useState('idle');
  const [stravaWebhook, setStravaWebhook] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [confirmDeleteActivity, setConfirmDeleteActivity] = useState(null);
  const [tagAtemschutz, setTagAtemschutz] = useState(null); // aKey der Activity die getaggt wird
  const [tagForm, setTagForm] = useState({ belastung: 'mittel', pa_kg: '' });

  useEffect(() => {
    apiFetch(API + '/dashboard').then(r => r.json()).then(setDash).catch(() => {});
    apiFetch(API + '/dashboard/woche').then(r => r.json()).then(setWoche).catch(() => {});
    apiFetch(API + '/dashboard/monat').then(r => r.json()).then(setMonat).catch(() => {});
    apiFetch(API + '/dashboard/jahr').then(r => r.json()).then(setJahr).catch(() => {});
  }, []);

  function handleDeleteAktivitaet(quelle, id) {
    const url = quelle === 'gym'
      ? API + '/gym/session/' + id
      : API + '/strava/aktivitaeten/' + id;
    apiFetch(url, { method: 'DELETE' })
      .then(() => {
        setConfirmDeleteActivity(null);
        setDash(d => d ? { ...d, letzteAktivitaeten: d.letzteAktivitaeten.filter(a => !(a.id === id && a.quelle === quelle)) } : d);
      })
      .catch(() => setConfirmDeleteActivity(null));
  }

  function handleAtemschutzTag(id, aktivAtemschutz) {
    if (aktivAtemschutz) {
      // Kennzeichnung entfernen
      apiFetch(API + '/strava/aktivitaeten/' + id + '/atemschutz', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ atemschutz: false }),
      }).then(() => {
        setTagAtemschutz(null);
        setDash(d => d ? { ...d, letzteAktivitaeten: d.letzteAktivitaeten.map(a => a.id === id && a.quelle === 'strava' ? { ...a, atemschutz: 0 } : a) } : d);
      });
    } else {
      // Kennzeichnen mit belastung + pa_kg
      apiFetch(API + '/strava/aktivitaeten/' + id + '/atemschutz', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ atemschutz: true, belastung: tagForm.belastung, pa_kg: tagForm.pa_kg ? parseInt(tagForm.pa_kg) : null }),
      }).then(() => {
        setTagAtemschutz(null);
        setDash(d => d ? { ...d, letzteAktivitaeten: d.letzteAktivitaeten.map(a => a.id === id && a.quelle === 'strava' ? { ...a, atemschutz: 1, atemschutz_belastung: tagForm.belastung } : a) } : d);
      });
    }
  }

  function stravaManualSync() {
    setStravaSync('syncing');
    apiFetch(API + '/strava/sync')
      .then(r => r.json())
      .then(d => {
        setStravaSync('done:' + (d.imported || 0));
        apiFetch(API + '/dashboard').then(r => r.json()).then(setDash).catch(() => {});
        setTimeout(() => setStravaSync('idle'), 4000);
      })
      .catch(() => { setStravaSync('error'); setTimeout(() => setStravaSync('idle'), 3000); });
  }

  function stravaCheckWebhook() {
    apiFetch(API + '/strava/webhook-status')
      .then(r => r.json())
      .then(d => setStravaWebhook(d.subscriptions || []))
      .catch(() => setStravaWebhook([]));
  }

  function stravaRegisterWebhook() {
    apiFetch(API + '/strava/webhook-register', { method: 'POST' })
      .then(r => r.json())
      .then(() => stravaCheckWebhook())
      .catch(() => {});
  }

  function stravaDeleteWebhook(id) {
    apiFetch(API + '/strava/webhook-delete/' + id, { method: 'DELETE' })
      .then(() => stravaCheckWebhook())
      .catch(() => {});
  }

  const Btn = ({id, l}) => (
    <button onClick={() => setTab(id)} style={{
      background: tab===id ? '#4a9c6f' : '#1a1a1a',
      color: tab===id ? '#fff' : '#666',
      border: '1px solid ' + (tab===id ? '#4a9c6f' : '#2a2a2a'),
      borderRadius: 6, padding: '6px 14px', fontSize: 12,
      cursor: 'pointer', fontFamily: 'inherit',
    }}>{l}</button>
  );

  return (
    <div style={{padding:16, maxWidth:680, margin:'0 auto', background:'#0d0d0d', minHeight:'100vh', fontFamily:'Georgia,serif', color:'#e0e0e0'}}>
      <div style={{marginBottom:16}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4}}>
          <div style={{fontSize:10, letterSpacing:4, color:'#555', textTransform:'uppercase'}}>Training</div>
          <button
            onClick={() => { localStorage.removeItem('appPasswordHash'); window.location.reload(); }}
            style={{ background:'transparent', border:'none', color:'#333', fontSize:11, cursor:'pointer', fontFamily:'inherit', padding:'2px 0' }}
          >
            Logout
          </button>
        </div>
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          <Btn id="ziele"     l="Ziele"/>
          <Btn id="plan"      l="Training"/>
          <Btn id="dashboard" l="Dashboard"/>
          <Btn id="koerper"   l="Körper"/>
        </div>
      </div>

      {tab === 'ziele'   && <Ziele/>}
      {tab === 'plan'    && <PlanTab/>}
      {tab === 'koerper' && <Koerper/>}

      {tab === 'dashboard' && !dash && <div style={{color:'#555', fontSize:13}}>Laden...</div>}
      {tab === 'dashboard' && dash && (
        <div>
          <HealthImportStatus/>
          {(woche || monat || jahr) && (
            <div style={{background:'#141414', borderRadius:8, padding:'12px 16px', marginBottom:16}}>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12}}>
                {woche && (
                  <div>
                    <div style={{fontSize:10, color:'#555', marginBottom:8, letterSpacing:2}}>WOCHE</div>
                    <div style={{display:'flex', flexDirection:'column', gap:8}}>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#4a7c59'}}>{woche.kraft?.anzahl || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>Gym</div>
                      </div>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#5a8fd4'}}>{woche.rad?.km || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>km Rad</div>
                      </div>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#5a8fd4'}}>{woche.rad?.stunden || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>h Rad</div>
                      </div>
                      {woche.tageSeitGym != null && (
                        <div>
                          <div style={{fontSize:20, fontWeight:700, color: woche.tageSeitGym > 5 ? '#c17f24' : '#888'}}>{woche.tageSeitGym}</div>
                          <div style={{fontSize:10, color: woche.tageSeitGym > 5 ? '#c17f24' : '#555'}}>d seit Gym</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {monat && (
                  <div style={{borderLeft:'1px solid #222', paddingLeft:12}}>
                    <div style={{fontSize:10, color:'#555', marginBottom:8, letterSpacing:2}}>MONAT</div>
                    <div style={{display:'flex', flexDirection:'column', gap:8}}>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#4a7c59'}}>{monat.kraft?.anzahl || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>Gym</div>
                      </div>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#5a8fd4'}}>{monat.rad?.km || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>km Rad</div>
                      </div>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#5a8fd4'}}>{monat.rad?.stunden || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>h Rad</div>
                      </div>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#888'}}>{monat.rad?.einheiten || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>Rides</div>
                      </div>
                    </div>
                  </div>
                )}
                {jahr && (
                  <div style={{borderLeft:'1px solid #222', paddingLeft:12}}>
                    <div style={{fontSize:10, color:'#555', marginBottom:8, letterSpacing:2}}>JAHR</div>
                    <div style={{display:'flex', flexDirection:'column', gap:8}}>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#4a7c59'}}>{jahr.kraft?.anzahl || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>Gym</div>
                      </div>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#5a8fd4'}}>{jahr.rad?.km || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>km Rad</div>
                      </div>
                      <div>
                        <div style={{fontSize:20, fontWeight:700, color:'#5a8fd4'}}>{jahr.rad?.stunden || 0}</div>
                        <div style={{fontSize:10, color:'#555'}}>h Rad</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <JahresVerlauf/>

          {dash.empfehlung && dash.empfehlung.empfehlung !== 'plan' && (
            <div style={{background:'#161616', borderLeft:'4px solid ' + (dash.empfehlung.empfehlung==='pause'?'#444':'#2a6fa8'), borderRadius:8, padding:'14px 18px', marginBottom:16}}>
              <div style={{fontSize:11, color:'#555', marginBottom:6}}>HEUTE — {new Date(dash.heute + 'T12:00:00').toLocaleDateString('de-DE', {weekday:'short', day:'numeric', month:'long'})}</div>
              <div style={{fontSize:14, fontWeight:700, color: dash.empfehlung.empfehlung==='pause'?'#888':'#2a6fa8', marginBottom:6}}>
                {dash.empfehlung.empfehlung==='zone2'?'Zone 2 / Erholung heute':'Pause'}
              </div>
              <div style={{fontSize:12, color:'#888'}}>{dash.empfehlung.begruendung}</div>
            </div>
          )}

          {dash.naechstesZiel && (() => {
            const z = dash.naechstesZiel;
            const istBestaerkung = z.modus !== 'ziel';
            const datStr = z.peak_datum ? new Date(z.peak_datum).toLocaleDateString('de-DE', {weekday:'short', day:'numeric', month:'long', year:'numeric', timeZone:'Europe/Berlin'}) : null;
            return (
              <div style={{background:'#141414', borderRadius:8, padding:'14px 18px', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:4}}>
                    <div style={{fontSize:11, color:'#555'}}>NÄCHSTES ZIEL</div>
                    <div style={{fontSize:9, color: istBestaerkung ? '#4a9c6f' : '#5a8fd4', border:`1px solid ${istBestaerkung ? '#2a5c2a' : '#2a3a6a'}`, borderRadius:3, padding:'1px 5px'}}>
                      {istBestaerkung ? '◯ Bestärkung' : '▸ Ziel'}
                    </div>
                  </div>
                  <div style={{fontSize:14, fontWeight:700, color:'#e0e0e0', marginBottom:2}}>{z.name}</div>
                  {datStr && <div style={{fontSize:11, color:'#666'}}>{datStr}</div>}
                  {istBestaerkung && (z.ziel_km || z.ziel_hm) && (
                    <div style={{fontSize:11, color:'#4a9c6f', marginTop:4}}>
                      Anforderung: {z.ziel_km ? `${z.ziel_km} km` : ''}{z.ziel_km && z.ziel_hm ? ' · ' : ''}{z.ziel_hm ? `${z.ziel_hm} hm` : ''}
                    </div>
                  )}
                </div>
                {/* Countdown nur im Ziel-Modus */}
                {!istBestaerkung && dash.tageBis != null && (
                  <div style={{textAlign:'right', marginLeft:12}}>
                    <div style={{fontSize:32, fontWeight:700, color:'#2a6fa8'}}>{dash.tageBis}</div>
                    <div style={{fontSize:11, color:'#555'}}>Tage</div>
                  </div>
                )}
              </div>
            );
          })()}

          <SchlafSnack />

          <div style={{background:'#141414', borderRadius:8, overflow:'hidden'}}>
            <div style={{padding:'12px 16px', background:'#1c1c1c', fontSize:12, color:'#888', borderBottom:'1px solid #222'}}>Letzte Aktivitäten</div>
            {dash.letzteAktivitaeten.map(a => {
              const aKey = `${a.quelle||'s'}-${a.id}`;
              const empf = ernaehrungsEmpfehlung(a.kcal_verbrauch);
              const isConfirmDelete = confirmDeleteActivity === aKey;
              const isTagging = tagAtemschutz === aKey;
              const istGetaggt = !!a.atemschutz;
              return (
                <div key={aKey}>
                  <div
                    onClick={() => { if (!isConfirmDelete && !isTagging) setSelectedActivity(selectedActivity === aKey ? null : aKey); }}
                    style={{padding:'12px 16px', borderBottom: (empf || isTagging) ? 'none' : '1px solid #1c1c1c', display:'grid', gridTemplateColumns:'1fr auto auto', alignItems:'center', gap:8, cursor: a.quelle==='gym' ? 'default' : 'pointer', background: selectedActivity === aKey ? '#161616' : 'transparent'}}
                  >
                    <div>
                      <div style={{fontSize:13, fontWeight:600, color:'#e0e0e0'}}>
                        {a.name}
                        {a.typ === 'VirtualRide' && <span style={{marginLeft:7, fontSize:9, color:'#7a6abf', background:'#1a1630', border:'1px solid #3a2a6a', borderRadius:3, padding:'1px 5px', verticalAlign:'middle', letterSpacing:0.5}}>Indoor</span>}
                        {a.has_streams ? <span style={{marginLeft:6, fontSize:9, color:'#2a5a8a', verticalAlign:'middle'}}>●</span> : null}
                      </div>
                      <div style={{fontSize:11, color:'#555', marginTop:2}}>
                        {new Date(a.datum).toLocaleDateString('de-DE', {weekday:'short', day:'numeric', month:'short', timeZone:'Europe/Berlin'})}
                        {a.dauer_sek ? ' · ' + Math.round(a.dauer_sek/60) + ' min' : ''}
                        {a.distanz_m ? ' · ' + (a.distanz_m/1000).toFixed(1) + ' km' : ''}
                      </div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      {/* HF-Kalorien-Schätzung (Keytel) wenn Apple-Wert fehlt/niedrig */}
                      {a.kcal_hf_schaetzung > 0
                        ? <div style={{fontSize:12, color:'#8a7a5a', marginBottom:1}} title="HF-Schätzung (Keytel)">{a.kcal_hf_schaetzung} kcal~</div>
                        : a.kcal_verbrauch > 0
                          ? <div style={{fontSize:12, color:'#8a7a5a', marginBottom:1}}>{Math.round(a.kcal_verbrauch)} kcal</div>
                          : null
                      }
                      {a.avg_hr      && <div style={{fontSize:12, color:'#c17f24'}}>{Math.round(a.avg_hr)} bpm</div>}
                      {a.avg_power   && <div style={{fontSize:12, color:'#4a9c6f'}}>{Math.round(a.avg_power)}W</div>}
                      {a.hm_aufstieg && <div style={{fontSize:11, color:'#8a6a3a'}}>{Math.round(a.hm_aufstieg)}m ↑</div>}
                    </div>
                    {/* Aktions-Buttons */}
                    <div onClick={e => e.stopPropagation()} style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3}}>
                      {/* Atemschutz-Tag (nur Strava) */}
                      {a.quelle === 'strava' && (
                        istGetaggt ? (
                          <button onClick={() => handleAtemschutzTag(a.id, true)}
                            title="Atemschutz-Kennzeichnung entfernen"
                            style={{background:'#1a0e0a', border:'1px solid #4a2a1a', borderRadius:4, color:'#c17f24', fontSize:11, padding:'2px 7px', cursor:'pointer', fontFamily:'inherit'}}>
                            🔥
                          </button>
                        ) : (
                          <button onClick={() => { setTagAtemschutz(isTagging ? null : aKey); setConfirmDeleteActivity(null); }}
                            title="Als Atemschutz kennzeichnen"
                            style={{background:'transparent', border:'1px solid #2a1a0a', borderRadius:4, color:'#4a3020', fontSize:11, padding:'2px 7px', cursor:'pointer', fontFamily:'inherit'}}>
                            🔥
                          </button>
                        )
                      )}
                      {/* Löschen */}
                      {isConfirmDelete ? (
                        <>
                          <button onClick={() => handleDeleteAktivitaet(a.quelle, a.id)}
                            style={{background:'#4a1a1a', border:'1px solid #8a3a3a', borderRadius:4, color:'#e06060', fontSize:11, padding:'3px 8px', cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap'}}>
                            Ja, löschen
                          </button>
                          <button onClick={() => setConfirmDeleteActivity(null)}
                            style={{background:'transparent', border:'none', color:'#555', fontSize:11, padding:'2px 4px', cursor:'pointer', fontFamily:'inherit'}}>
                            Abbrechen
                          </button>
                        </>
                      ) : (
                        <button onClick={() => { setConfirmDeleteActivity(aKey); setTagAtemschutz(null); }}
                          style={{background:'transparent', border:'none', color:'#4a2a2a', fontSize:16, padding:'2px 4px', cursor:'pointer', lineHeight:1}}>
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Atemschutz-Tagging-Panel */}
                  {isTagging && (
                    <div onClick={e => e.stopPropagation()} style={{padding:'10px 16px', background:'#0e0a06', borderBottom:'1px solid #2a1a0a', borderLeft:'3px solid #c17f24'}}>
                      <div style={{fontSize:10, color:'#c17f24', letterSpacing:2, marginBottom:8}}>ATEMSCHUTZ KENNZEICHNEN</div>
                      <div style={{display:'flex', gap:6, marginBottom:8}}>
                        {['leicht','mittel','schwer'].map(b => (
                          <button key={b} onClick={() => setTagForm(f => ({...f, belastung: b}))}
                            style={{flex:1, background: tagForm.belastung===b ? '#2a1a0a' : '#111', border:`1px solid ${tagForm.belastung===b ? '#c17f24' : '#2a2a2a'}`, borderRadius:4, padding:'5px 4px', fontSize:10, color: tagForm.belastung===b ? '#c17f24' : '#555', cursor:'pointer', fontFamily:'inherit'}}>
                            {b.charAt(0).toUpperCase()+b.slice(1)}
                          </button>
                        ))}
                      </div>
                      <div style={{display:'flex', gap:8, alignItems:'center'}}>
                        <input type="number" min="10" max="30" step="0.5" value={tagForm.pa_kg} onChange={e => setTagForm(f => ({...f, pa_kg: e.target.value}))}
                          placeholder="PA-Gewicht kg"
                          style={{flex:1, background:'#111', border:'1px solid #2a2a2a', borderRadius:4, padding:'6px 8px', color:'#e0e0e0', fontSize:12, fontFamily:'inherit', outline:'none'}} />
                        <button onClick={() => handleAtemschutzTag(a.id, false)}
                          style={{background:'#2a1a0a', border:'1px solid #c17f24', borderRadius:5, color:'#c17f24', fontSize:12, fontWeight:700, padding:'6px 14px', cursor:'pointer', fontFamily:'inherit'}}>
                          🔥 Kennzeichnen
                        </button>
                        <button onClick={() => setTagAtemschutz(null)}
                          style={{background:'transparent', border:'none', color:'#555', fontSize:12, cursor:'pointer', fontFamily:'inherit'}}>
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}
                  {empf && (
                    <div style={{padding:'8px 16px', background: empf.bg, borderBottom:'1px solid #1c1c1c', fontSize:11, color: empf.farbe, borderLeft:`3px solid ${empf.farbe}`}}>
                      {empf.text}
                    </div>
                  )}
                  {selectedActivity === aKey && a.quelle !== 'gym' && (
                    <div style={{padding:'10px 12px', borderBottom:'1px solid #1c1c1c'}}>
                      <StravaChart activityId={a.id} onClose={() => setSelectedActivity(null)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ─── Strava-Verwaltung ─── */}
          <div style={{background:'#141414', borderRadius:8, overflow:'hidden', marginTop:16}}>
            <div style={{padding:'12px 16px', background:'#1c1c1c', fontSize:12, color:'#888', borderBottom:'1px solid #222'}}>Strava</div>
            <div style={{padding:'14px 16px', display:'flex', flexDirection:'column', gap:10}}>
              <button
                onClick={stravaManualSync}
                disabled={stravaSync === 'syncing'}
                style={{background: stravaSync.startsWith('done') ? '#2a5c3a' : stravaSync === 'error' ? '#5c2a2a' : '#1c2c1c', border:'1px solid #2a4a2a', borderRadius:6, padding:'10px 16px', color: stravaSync.startsWith('done') ? '#7fcf9f' : stravaSync === 'error' ? '#cf7f7f' : '#5abf7a', fontSize:13, cursor:'pointer', fontFamily:'inherit', textAlign:'left'}}
              >
                {stravaSync === 'syncing' ? '⏳ Synchronisiere...' : stravaSync.startsWith('done') ? `✓ ${stravaSync.split(':')[1]} Aktivitäten importiert` : stravaSync === 'error' ? '✗ Fehler beim Sync' : '↓ Letzte 30 Tage manuell sync'}
              </button>
              <button
                onClick={stravaCheckWebhook}
                style={{background:'#1a1a2e', border:'1px solid #2a2a4a', borderRadius:6, padding:'8px 16px', color:'#6a8acf', fontSize:12, cursor:'pointer', fontFamily:'inherit', textAlign:'left'}}
              >Webhook-Status prüfen</button>
              {stravaWebhook !== null && (
                <div style={{background:'#111', borderRadius:6, padding:'10px 14px', fontSize:12}}>
                  {stravaWebhook.length === 0 ? (
                    <div style={{color:'#cf7f50'}}>
                      Kein Webhook aktiv — automatischer Sync deaktiviert.
                      <button onClick={stravaRegisterWebhook} style={{marginLeft:12, background:'#2a4a2a', border:'1px solid #4a7a4a', borderRadius:4, padding:'4px 10px', color:'#7fcf9f', fontSize:11, cursor:'pointer', fontFamily:'inherit'}}>Registrieren</button>
                    </div>
                  ) : stravaWebhook.map(s => (
                    <div key={s.id} style={{color:'#7fcf9f', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                      <span>✓ Webhook aktiv (ID {s.id})</span>
                      <button onClick={() => stravaDeleteWebhook(s.id)} style={{background:'transparent', border:'1px solid #3a2020', color:'#804040', borderRadius:4, padding:'3px 8px', fontSize:11, cursor:'pointer', fontFamily:'inherit'}}>löschen</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Root() {
  return <AuthGate><App /></AuthGate>;
}
